/**
 * @fileoverview Modal de importação de contatos (planilha .xlsx ou agenda WhatsApp)
 * com progresso em tempo real via Socket.IO.
 * Paleta alinhada ao ContactModal.
 */

import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Dialog,
  IconButton,
  Typography,
  LinearProgress,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { Upload, Download, FileSpreadsheet, MessageCircle, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, FileCheck, X } from "lucide-react";
import * as XLSX from "xlsx";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { SocketContext } from "../../context/Socket/SocketContext";

const MODAL_FONT_FAMILY = '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif';

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    dialog: {
      "& .MuiDialog-paper": {
        fontFamily: MODAL_FONT_FAMILY,
        width: "min(560px, calc(100vw - 32px))",
        maxWidth: "calc(100vw - 32px)",
        borderRadius: 24,
        overflow: "hidden",
        background: dark
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
        boxShadow: dark
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
        border: dark
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
        "& *": { fontFamily: MODAL_FONT_FAMILY },
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2.5, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    },
    title: {
      fontSize: "1.35rem",
      fontWeight: 700,
      color: dark ? "#f8fafc" : "#111827",
      lineHeight: 1.2,
    },
    close: {
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
    },
    body: {
      padding: theme.spacing(3),
      maxHeight: "calc(min(88vh, 720px) - 136px)",
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    segmented: {
      display: "flex",
      gap: 4,
      padding: 4,
      borderRadius: 14,
      background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)",
      border: dark ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid rgba(15, 23, 42, 0.08)",
      marginBottom: theme.spacing(2.5),
    },
    segBtn: {
      flex: 1,
      height: 36,
      border: "none",
      background: "transparent",
      borderRadius: 10,
      cursor: "pointer",
      fontSize: "0.88rem",
      fontWeight: 600,
      color: dark ? "rgba(248, 250, 252, 0.6)" : "#64748b",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "inherit",
      "&:hover": {
        color: dark ? "#f8fafc" : "#111827",
      },
    },
    segBtnActive: {
      background: dark ? "rgba(91, 124, 255, 0.16)" : "#ffffff",
      color: dark ? "#a5b4fc" : "#3b82f6",
      boxShadow: dark
        ? "0 1px 3px rgba(0, 0, 0, 0.2)"
        : "0 1px 3px rgba(15, 23, 42, 0.08)",
    },
    dropZone: {
      display: "block",
      border: dark
        ? "2px dashed rgba(91, 124, 255, 0.3)"
        : "2px dashed rgba(15, 23, 42, 0.15)",
      borderRadius: 16,
      padding: theme.spacing(4, 3),
      textAlign: "center",
      cursor: "pointer",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      background: dark ? "rgba(255, 255, 255, 0.02)" : "rgba(248, 250, 252, 0.6)",
      "&:hover": {
        borderColor: dark ? "rgba(91, 124, 255, 0.55)" : "rgba(59, 130, 246, 0.4)",
        background: dark ? "rgba(91, 124, 255, 0.06)" : "rgba(59, 130, 246, 0.03)",
      },
    },
    dropIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 48,
      height: 48,
      borderRadius: 14,
      background: dark
        ? "linear-gradient(135deg, rgba(91, 124, 255, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)"
        : "linear-gradient(135deg, rgba(79, 124, 255, 0.12) 0%, rgba(59, 130, 246, 0.10) 100%)",
      color: dark ? "#a5b4fc" : "#3b82f6",
      marginBottom: theme.spacing(1.5),
    },
    dropText: {
      fontWeight: 600,
      fontSize: "0.92rem",
      color: dark ? "#f8fafc" : "#111827",
      marginBottom: 4,
    },
    dropHint: {
      fontSize: "0.78rem",
      color: dark ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
    },
    fileInfo: {
      marginTop: theme.spacing(2),
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 12px",
      borderRadius: 999,
      background: dark ? "rgba(74, 222, 128, 0.12)" : "rgba(22, 163, 74, 0.08)",
      color: dark ? "#4ade80" : "#15803d",
      fontWeight: 600,
      fontSize: "0.82rem",
    },
    hiddenInput: { display: "none" },
    whatsappForm: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    whatsappHint: {
      fontSize: "0.85rem",
      color: dark ? "rgba(248, 250, 252, 0.6)" : "#64748b",
      lineHeight: 1.5,
    },
    selectField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 14,
        background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)",
        border: dark
          ? "1px solid rgba(255, 255, 255, 0.12)"
          : "1px solid rgba(15, 23, 42, 0.12)",
        "& fieldset": { border: "none" },
        "&.Mui-focused": {
          border: dark ? "1px solid #7aa2ff" : "1px solid #3b82f6",
          boxShadow: dark
            ? "0 0 0 3px rgba(122, 162, 255, 0.15)"
            : "0 0 0 3px rgba(59, 130, 246, 0.12)",
        },
      },
    },
    progressWrap: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
    progressHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    progressText: {
      fontSize: "0.9rem",
      fontWeight: 600,
      color: dark ? "#e2e8f0" : "#334155",
    },
    progressPercentage: {
      fontSize: "1.05rem",
      fontWeight: 700,
      color: dark ? "#a5b4fc" : "#3b82f6",
    },
    progressBar: {
      height: 10,
      borderRadius: 8,
      backgroundColor: dark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.06)",
      "& .MuiLinearProgress-bar": {
        borderRadius: 8,
        background: "linear-gradient(90deg, #4f7cff 0%, #3b82f6 100%)",
        transition: "transform 0.4s ease",
      },
    },
    counters: {
      display: "flex",
      gap: theme.spacing(2.5),
      marginTop: theme.spacing(1),
    },
    counterItem: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.82rem",
      fontWeight: 600,
    },
    successCounter: { color: dark ? "#4ade80" : "#16a34a" },
    errorCounter: { color: dark ? "#f87171" : "#dc2626" },
    resultCard: {
      marginTop: theme.spacing(2),
      borderRadius: 14,
      overflow: "hidden",
    },
    resultHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(1.5, 2),
    },
    resultSuccess: {
      background: dark ? "rgba(74, 222, 128, 0.08)" : "rgba(22, 163, 74, 0.06)",
      border: dark ? "1px solid rgba(74, 222, 128, 0.2)" : "1px solid rgba(22, 163, 74, 0.18)",
    },
    resultError: {
      background: dark ? "rgba(248, 113, 113, 0.08)" : "rgba(220, 38, 38, 0.06)",
      border: dark ? "1px solid rgba(248, 113, 113, 0.22)" : "1px solid rgba(220, 38, 38, 0.2)",
    },
    resultTitle: {
      fontWeight: 700,
      fontSize: "0.88rem",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
    },
    errorList: {
      margin: 0,
      padding: theme.spacing(1, 2.5, 1.5, 3.5),
      maxHeight: 160,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
      "& li": {
        fontSize: "0.78rem",
        color: dark ? "#fca5a5" : "#991b1b",
        padding: "3px 0",
      },
    },
    errorToggle: { cursor: "pointer", "&:hover": { opacity: 0.85 } },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      padding: theme.spacing(2, 3, 2.5),
      borderTop: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    },
    btnBase: {
      height: 36,
      minWidth: 120,
      padding: "0 18px",
      borderRadius: 10,
      fontSize: "0.9rem",
      fontWeight: 500,
      cursor: "pointer",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "inherit",
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      border: "none",
      boxShadow: "none",
      "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
    },
    btnGhost: {
      background: "transparent",
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
        color: dark ? "#f8fafc" : "#111827",
      },
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
    },
  };
});

const STATES = {
  UPLOAD: "upload",
  IMPORTING: "importing",
  COMPLETE: "complete",
};

const ImportContactsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);

  const [modalState, setModalState] = useState(STATES.UPLOAD);
  const [nameFile, setNameFile] = useState("");
  const [listContacts, setListContacts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [whatsapps, setWhatsapps] = useState([]);
  const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [includeUnsaved, setIncludeUnsaved] = useState(false);
  const [progress, setProgress] = useState({
    processed: 0,
    total: 0,
    percentage: 0,
    successCount: 0,
    errorCount: 0,
  });
  const [result, setResult] = useState(null);
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const socketRef = useRef(null);
  const listenersRef = useRef({ progress: null, complete: null });

  const cleanupSocket = useCallback(() => {
    const companyId = localStorage.getItem("companyId");
    if (socketRef.current && companyId) {
      if (listenersRef.current.progress) {
        socketRef.current.off(
          `company-${companyId}-contact-import-progress`,
          listenersRef.current.progress
        );
      }
      if (listenersRef.current.complete) {
        socketRef.current.off(
          `company-${companyId}-contact-import-complete`,
          listenersRef.current.complete
        );
      }
      listenersRef.current = { progress: null, complete: null };
    }
  }, []);

  useEffect(() => {
    return () => cleanupSocket();
  }, [cleanupSocket]);

  const resetModal = useCallback(() => {
    cleanupSocket();
    setModalState(STATES.UPLOAD);
    setNameFile("");
    setListContacts([]);
    setProgress({ processed: 0, total: 0, percentage: 0, successCount: 0, errorCount: 0 });
    setResult(null);
    setErrorsExpanded(false);
    setActiveTab(0);
    setSelectedWhatsappId("");
    setIncludeUnsaved(false);
  }, [cleanupSocket]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingWhatsapps(true);
    api
      .get("/whatsapp/")
      .then((res) => {
        if (cancelled) return;
        const list = (res.data || []).filter(
          (w) => w.provider === "waha" && w.status === "CONNECTED"
        );
        setWhatsapps(list);
        if (list.length === 1) setSelectedWhatsappId(list[0].id);
      })
      .catch(() => {
        if (!cancelled) setWhatsapps([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingWhatsapps(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = useCallback(() => {
    if (modalState === STATES.IMPORTING) {
      onClose();
      return;
    }
    resetModal();
    onClose();
  }, [modalState, onClose, resetModal]);

  useEffect(() => {
    if (open) resetModal();
  }, [open, resetModal]);

  const handleNewFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNameFile(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const ab = evt.target.result;
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);
      setListContacts(data);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const attachImportListeners = useCallback(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    socketRef.current = socket;

    const handleProgress = (data) => {
      setProgress({
        processed: data.processed,
        total: data.total,
        percentage: data.percentage,
        successCount: data.successCount,
        errorCount: data.errorCount,
      });
    };

    const handleComplete = (data) => {
      setResult(data);
      setModalState(STATES.COMPLETE);
      setProgress((prev) => ({ ...prev, percentage: 100, processed: prev.total }));
    };

    listenersRef.current = { progress: handleProgress, complete: handleComplete };
    socket.on(`company-${companyId}-contact-import-progress`, handleProgress);
    socket.on(`company-${companyId}-contact-import-complete`, handleComplete);
  }, [socketManager]);

  const handleImport = async () => {
    if (listContacts.length === 0) return;
    setModalState(STATES.IMPORTING);
    setProgress({
      processed: 0,
      total: listContacts.length,
      percentage: 0,
      successCount: 0,
      errorCount: 0,
    });
    attachImportListeners();
    try {
      await api.post("/contacts/upload", listContacts);
    } catch (err) {
      toastError(err);
      setModalState(STATES.UPLOAD);
      cleanupSocket();
    }
  };

  const handleImportFromWhatsapp = async () => {
    if (!selectedWhatsappId) return;
    setModalState(STATES.IMPORTING);
    setProgress({ processed: 0, total: 0, percentage: 0, successCount: 0, errorCount: 0 });
    attachImportListeners();
    try {
      await api.post("/contacts/import-from-whatsapp", {
        whatsappId: selectedWhatsappId,
        includeUnsaved,
      });
    } catch (err) {
      toastError(err);
      setModalState(STATES.UPLOAD);
      cleanupSocket();
    }
  };

  const handleDownloadModel = () => {
    window.location.href = `${window.location.protocol}//${window.location.host}/import-contatos.xlsx`;
  };

  const formatNumber = (n) => n.toLocaleString("pt-BR");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      scroll="paper"
      className={classes.dialog}
    >
      <div className={classes.header}>
        <Typography className={classes.title}>
          {modalState === STATES.COMPLETE
            ? i18n.t("contactImportModal.progress.complete")
            : i18n.t("contactImportModal.title")}
        </Typography>
        <IconButton size="small" onClick={handleClose} className={classes.close}>
          <X size={18} />
        </IconButton>
      </div>

      <div className={classes.body}>
        {modalState === STATES.UPLOAD && (
          <>
            <div className={classes.segmented}>
              <button
                type="button"
                className={`${classes.segBtn} ${activeTab === 0 ? classes.segBtnActive : ""}`}
                onClick={() => setActiveTab(0)}
              >
                <FileSpreadsheet size={15} strokeWidth={1.75} />
                Planilha
              </button>
              <button
                type="button"
                className={`${classes.segBtn} ${activeTab === 1 ? classes.segBtnActive : ""}`}
                onClick={() => setActiveTab(1)}
              >
                <MessageCircle size={15} strokeWidth={1.75} />
                Conexão WhatsApp
              </button>
            </div>

            {activeTab === 0 && (
              <>
                <label htmlFor="i-import-contacts" className={classes.dropZone}>
                  <span className={classes.dropIcon}>
                    <Upload size={22} strokeWidth={1.75} />
                  </span>
                  <div className={classes.dropText}>
                    {i18n.t("contactImportModal.labels.import")}
                  </div>
                  <div className={classes.dropHint}>
                    Arraste ou clique para selecionar um arquivo .xlsx
                  </div>
                  {nameFile && (
                    <div className={classes.fileInfo}>
                      <FileCheck size={13} strokeWidth={2} />
                      {nameFile} — {formatNumber(listContacts.length)}{" "}
                      {i18n.t("contactImportModal.labels.result")}
                    </div>
                  )}
                </label>
                <input
                  onChange={handleNewFile}
                  className={classes.hiddenInput}
                  type="file"
                  accept=".xlsx"
                  id="i-import-contacts"
                />
              </>
            )}

            {activeTab === 1 && (
              <div className={classes.whatsappForm}>
                <Typography className={classes.whatsappHint}>
                  Importa todos os contatos da agenda do número conectado. A operação
                  roda em segundo plano — você pode fechar o modal e acompanhar pela
                  barra de progresso.
                </Typography>

                <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
                  <InputLabel id="waha-conn-label">Conexão WhatsApp</InputLabel>
                  <Select
                    labelId="waha-conn-label"
                    label="Conexão WhatsApp"
                    value={selectedWhatsappId}
                    onChange={(e) => setSelectedWhatsappId(e.target.value)}
                    disabled={loadingWhatsapps || whatsapps.length === 0}
                  >
                    {whatsapps.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name} {w.number ? `(${w.number})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {loadingWhatsapps && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Carregando conexões...</Typography>
                  </div>
                )}
                {!loadingWhatsapps && whatsapps.length === 0 && (
                  <Typography variant="caption" color="error">
                    Nenhuma conexão WAHA conectada disponível.
                  </Typography>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeUnsaved}
                      onChange={(e) => setIncludeUnsaved(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Incluir contatos não salvos na agenda
                    </Typography>
                  }
                />
              </div>
            )}
          </>
        )}

        {modalState === STATES.IMPORTING && (
          <div className={classes.progressWrap}>
            <div className={classes.progressHeader}>
              <Typography className={classes.progressText}>
                {i18n
                  .t("contactImportModal.progress.importing")
                  .replace("{{processed}}", formatNumber(progress.processed))
                  .replace("{{total}}", formatNumber(progress.total))}
              </Typography>
              <Typography className={classes.progressPercentage}>
                {progress.percentage}%
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={progress.percentage}
              className={classes.progressBar}
            />
            <div className={classes.counters}>
              <div className={`${classes.counterItem} ${classes.successCounter}`}>
                <CheckCircle2 size={15} strokeWidth={1.75} />
                {formatNumber(progress.successCount)}{" "}
                {i18n.t("contactImportModal.progress.success").replace("{{count}}", "")}
              </div>
              <div className={`${classes.counterItem} ${classes.errorCounter}`}>
                <AlertCircle size={15} strokeWidth={1.75} />
                {formatNumber(progress.errorCount)}{" "}
                {i18n.t("contactImportModal.progress.errors").replace("{{count}}", "")}
              </div>
            </div>
          </div>
        )}

        {modalState === STATES.COMPLETE && result && (
          <>
            <LinearProgress
              variant="determinate"
              value={100}
              className={classes.progressBar}
            />

            {result.successCount > 0 && (
              <div className={`${classes.resultCard} ${classes.resultSuccess}`}>
                <div className={classes.resultHeader}>
                  <Typography className={`${classes.resultTitle} ${classes.successCounter}`}>
                    <CheckCircle2 size={16} strokeWidth={1.75} />
                    {i18n
                      .t("contactImportModal.progress.success")
                      .replace("{{count}}", formatNumber(result.successCount))}
                  </Typography>
                </div>
              </div>
            )}

            {result.errorCount > 0 && (
              <div className={`${classes.resultCard} ${classes.resultError}`}>
                <div
                  className={`${classes.resultHeader} ${classes.errorToggle}`}
                  onClick={() => setErrorsExpanded(!errorsExpanded)}
                >
                  <Typography className={`${classes.resultTitle} ${classes.errorCounter}`}>
                    <AlertCircle size={16} strokeWidth={1.75} />
                    {i18n
                      .t("contactImportModal.progress.errors")
                      .replace("{{count}}", formatNumber(result.errorCount))}
                  </Typography>
                  {errorsExpanded ? (
                    <ChevronUp size={15} strokeWidth={1.75} className={classes.errorCounter} />
                  ) : (
                    <ChevronDown size={15} strokeWidth={1.75} className={classes.errorCounter} />
                  )}
                </div>
                <Collapse in={errorsExpanded}>
                  <ul className={classes.errorList}>
                    {result.errors.map((err, idx) => (
                      <li key={idx}>
                        {err.contactName} — {err.error}
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </div>
            )}
          </>
        )}
      </div>

      <div className={classes.actions}>
        {modalState === STATES.UPLOAD && activeTab === 0 && (
          <>
            <button
              type="button"
              className={`${classes.btnBase} ${classes.btnGhost}`}
              onClick={handleDownloadModel}
            >
              <Download size={15} strokeWidth={1.75} />
              {i18n.t("contactImportModal.buttons.download")}
            </button>
            <button
              type="button"
              className={`${classes.btnBase} ${classes.btnPrimary}`}
              onClick={handleImport}
              disabled={listContacts.length === 0}
            >
              <Upload size={15} strokeWidth={1.75} />
              {i18n.t("contactImportModal.buttons.import")}
            </button>
          </>
        )}

        {modalState === STATES.UPLOAD && activeTab === 1 && (
          <button
            type="button"
            className={`${classes.btnBase} ${classes.btnPrimary}`}
            onClick={handleImportFromWhatsapp}
            disabled={!selectedWhatsappId}
          >
            <MessageCircle size={15} strokeWidth={1.75} />
            Importar agenda
          </button>
        )}

        {modalState === STATES.IMPORTING && (
          <button
            type="button"
            className={`${classes.btnBase} ${classes.btnGhost}`}
            onClick={handleClose}
          >
            {i18n.t("contactImportModal.buttons.background")}
          </button>
        )}

        {modalState === STATES.COMPLETE && (
          <button
            type="button"
            className={`${classes.btnBase} ${classes.btnPrimary}`}
            onClick={handleClose}
          >
            {i18n.t("contactImportModal.buttons.close")}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default ImportContactsModal;
